import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';


import { gnnApi } from '../services/api';

const GRADIENT_COLORS = [
  '#6366f1', '#8b5cf6', '#a78bfa', '#22d3ee',
  '#10b981', '#f59e0b', '#f43f5e',
];

export const ClassDistribution: React.FC = () => {
  const [data, setData] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [viewType, setViewType] = useState<'bar' | 'pie'>('bar');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    loadDistribution();
  }, []);

  const loadDistribution = async () => {
    try {
      setError(false);
      const response = await gnnApi.getClassDistribution();
      const chartData = response.data.labels.map((label: string, idx: number) => ({
        name: label,
        count: response.data.values[idx],
      }));
      setData(chartData);
    } catch (err) {
      console.error('Error loading class distribution:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container glass-card">
        <h3 className="mb-4 flex items-center gap-2 font-bold">
          <span className="icon-fallback text-accent-violet">📊</span>
          Class Distribution
        </h3>
        <div className="skeleton skeleton-chart rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container glass-card">
        <h3 className="mb-4 flex items-center gap-2 font-bold">
          <span className="icon-fallback text-accent-violet">📊</span>
          Class Distribution
        </h3>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#839493' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
          <p style={{ fontSize: '0.85rem' }}>Could not load data — is the backend running?</p>
          <button
            onClick={loadDistribution}
            style={{ marginTop: '1rem', padding: '0.4rem 1rem', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#b9cac9', cursor: 'pointer', fontSize: '0.8rem' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="chart-container glass-card relative overflow-hidden"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="absolute top-0 right-0 w-64 h-64 bg-accent-violet rounded-full blur-[100px] opacity-10 pointer-events-none" />
      
      <div className="flex justify-between items-center mb-6">
        <h3 className="m-0 flex items-center gap-2 font-bold text-lg">
          <span className="p-1.5 rounded-lg bg-[rgba(139,92,246,0.1)] text-accent-violet">
            <span className="icon-fallback"  >📊</span>
          </span>
          Class Distribution
        </h3>
        
        <div className="flex bg-[rgba(15,23,42,0.5)] rounded-lg p-1 border border-border-subtle">
          <button 
            className={`p-1.5 rounded-md transition-colors ${viewType === 'bar' ? 'bg-surface text-accent-blue shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
            onClick={() => setViewType('bar')}
            title="Bar Chart"
          >
            <span className="icon-fallback"  >📊</span>
          </button>
          <button 
            className={`p-1.5 rounded-md transition-colors ${viewType === 'pie' ? 'bg-surface text-accent-blue shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
            onClick={() => setViewType('pie')}
            title="Donut Chart"
          >
            <span className="icon-fallback"  >🍩</span>
          </button>
        </div>
      </div>

      <div className="chart-wrapper relative h-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={viewType}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full absolute inset-0"
          >
            <ResponsiveContainer width="100%" height="100%">
              {viewType === 'bar' ? (
                <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(148,163,184,0.15)' }}
                    tickLine={false}
                    angle={-25}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(148,163,184,0.15)' }}
                    tickLine={false}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'rgba(30,41,59,0.9)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(148,163,184,0.15)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                      color: '#f1f5f9',
                      fontSize: '0.85rem',
                    }}
                    cursor={{ fill: 'rgba(0,251,251,0.08)' }}
                  />
                  <Bar 
                    dataKey="count" 
                    radius={[6, 6, 0, 0]} 
                    maxBarSize={40}
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    {data.map((_entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={GRADIENT_COLORS[index % GRADIENT_COLORS.length]}
                        strokeWidth={activeIndex === index ? 2 : 0}
                        stroke="#fff"
                        style={{
                          transition: 'all 0.3s ease',
                          filter: activeIndex === index ? `drop-shadow(0 0 8px ${GRADIENT_COLORS[index % GRADIENT_COLORS.length]}80)` : 'none'
                        }}
                      />
                    ))}
                  </Bar>
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={4}
                    dataKey="count"
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    {data.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={GRADIENT_COLORS[index % GRADIENT_COLORS.length]}
                        strokeWidth={activeIndex === index ? 2 : 0}
                        stroke="#fff"
                        style={{
                          transition: 'all 0.3s ease',
                          filter: activeIndex === index ? `drop-shadow(0 0 8px ${GRADIENT_COLORS[index % GRADIENT_COLORS.length]}80)` : 'none',
                          transformOrigin: 'center',
                          transform: activeIndex === index ? 'scale(1.05)' : 'scale(1)'
                        }}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{
                      background: 'rgba(30,41,59,0.9)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(148,163,184,0.15)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                      color: '#f1f5f9',
                      fontSize: '0.85rem',
                    }}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
